import { FC, useCallback, useEffect } from 'react';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Box, Button, Flex, HStack, Text, VStack } from '@bonusly/design-system';

import { useLinearAuthorize, useLinearDisconnect, useLinearStatus } from '../../queries/linear';
import { ConnectorAccessNote } from '../bizy-settings/connector-access-note';
import { LinearLogo } from './linear-logo';

interface LinearConnectCardProps {
	returnPath?: string;
}

export const LinearConnectCard: FC<LinearConnectCardProps> = ({ returnPath }) => {
	const { data: status, isLoading: isLoadingStatus } = useLinearStatus();
	const { mutateAsync: authorize, isLoading: isAuthorizing } = useLinearAuthorize();
	const { mutateAsync: disconnect, isLoading: isDisconnecting } = useLinearDisconnect();
	const { track } = useAnalytics();

	const connectionStatus = status?.status;

	useEffect(() => {
		if (connectionStatus === 'connected' && sessionStorage.getItem('bizy_connecting_linear')) {
			track(AnalyticsEvent.Linear.Connected);
			sessionStorage.removeItem('bizy_connecting_linear');
		}
	}, [connectionStatus, track]);

	const handleConnect = useCallback(async () => {
		track(AnalyticsEvent.Linear.ConnectClicked);
		try {
			const { redirectUrl } = await authorize(returnPath ?? window.location.pathname);
			if (redirectUrl) {
				sessionStorage.setItem('bizy_connecting_linear', 'true');
				window.location.href = redirectUrl;
			}
		} catch {
			// Error handled by React Query
		}
	}, [authorize, returnPath, track]);

	const handleDisconnect = useCallback(async () => {
		try {
			await disconnect();
			track(AnalyticsEvent.Linear.Disconnected);
		} catch {
			// Error handled by React Query
		}
	}, [disconnect, track]);

	if (isLoadingStatus) return null;
	const isConnected = connectionStatus === 'connected';
	const isExpired = connectionStatus === 'expired';

	const statusLabel = isConnected ? 'Connected' : isExpired ? 'Reconnect required' : 'Not connected';
	const statusColor = isExpired ? 'text.warning' : 'text.softer';

	return (
		<Box py={3} borderBottom="1px solid" borderColor="border.soft">
			<Flex alignItems="center" justifyContent="space-between">
				<HStack spacing={3} align="center">
					<Box color="text.default" flexShrink={0} display="inline-flex" alignItems="center">
						<LinearLogo width="24" height="24" />
					</Box>
					<VStack spacing={0} align="flex-start">
						<Text size="md" variant="strong" color="text.default">
							Linear
						</Text>
						<Text size="sm" color={statusColor}>
							{statusLabel}
						</Text>
					</VStack>
				</HStack>
				{isConnected ? (
					<Button size="md" variant="ghost" colorScheme="secondary" onClick={handleDisconnect} isLoading={isDisconnecting}>
						Disconnect
					</Button>
				) : (
					<Button size="md" colorScheme="primary" onClick={handleConnect} isLoading={isAuthorizing}>
						{isExpired ? 'Reconnect' : 'Connect'}
					</Button>
				)}
			</Flex>
			<ConnectorAccessNote connector="linear" />
		</Box>
	);
};
